// src/components/Prescriptions/AddPrescriptionModal.jsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  Button,
  InputNumber,
  Spin,
  App,
  Card,
} from "antd";
import {
  CloseOutlined,
  SaveOutlined,
  SendOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  apiCreatePrescription,
  apiUpdatePrescription,
  apiGetPrescription,
  apiGetMedications,
  apiGetPatients,
} from "../../api/prescription.js";
import "./AddPrescriptionModal.css";

const { Option } = Select;
const { TextArea } = Input;

const AddPrescriptionModal = ({
  visible,
  prescriptionId,
  onClose,
  onSuccess,
  pharmacy,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchingPrescription, setFetchingPrescription] = useState(false);
  const [patients, setPatients] = useState([]);
  const [medications, setMedications] = useState([]);
  const [medicationFields, setMedicationFields] = useState([
    { id: Date.now() },
  ]);
  const [searchingMedications, setSearchingMedications] = useState(false);
  const [medicationSearchTimeout, setMedicationSearchTimeout] = useState(null);
  const [currentPrescription, setCurrentPrescription] = useState(null); // store fetched prescription
  const [discountInput, setDiscountInput] = useState(0); // Track discount input for real-time display

  // Calculate totals for display and validation
  const calculateTotals = (customDiscount) => {
    // If editing an existing prescription with pricing data from API
    if (isEditMode && currentPrescription) {
      // First try to use the direct pricing fields from API
      if (currentPrescription.totalPrice !== undefined) {
        const totalPrice = currentPrescription.totalPrice || 0;
        const discountAmount = customDiscount !== undefined ? customDiscount : (form.getFieldValue('discountAmount') || currentPrescription.discountAmount || 0);
        const finalPrice = Math.max(0, totalPrice - discountAmount);

        return { totalPrice, discountAmount, finalPrice };
      }

      // If no direct pricing, calculate from items
      if (currentPrescription.items && currentPrescription.items.length > 0) {
        const totalPrice = currentPrescription.items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
        const discountAmount = customDiscount !== undefined ? customDiscount : (form.getFieldValue('discountAmount') || 0);
        const finalPrice = Math.max(0, totalPrice - discountAmount);

        return { totalPrice, discountAmount, finalPrice };
      }
    }

    // For new prescriptions, calculate from medication data
    let totalPrice = 0;

    medicationFields.forEach(field => {
      const medicationId = form.getFieldValue(`medication_${field.id}`);
      const dosageAmount = form.getFieldValue(`dosageAmount_${field.id}`) || 1;
      const medication = medications.find(m => m.id === medicationId);


      if (medication && medication.unitPrice) {
        totalPrice += medication.unitPrice * dosageAmount;
      }
    });

    const discountAmount = customDiscount !== undefined ? customDiscount : (form.getFieldValue('discountAmount') || 0);
    const finalPrice = Math.max(0, totalPrice - discountAmount);

    return { totalPrice, discountAmount, finalPrice };
  };

  const isEditMode = !!prescriptionId;

  // Check if current prescription is in status=3 (!isPremium) - fields should be disabled
  const isPaidNonPremium = isEditMode && currentPrescription?.status === 3 && !Boolean(currentPrescription?.isPremium);

  useEffect(() => {
    if (visible) {
      fetchPatients();
      fetchMedications();

      if (prescriptionId) {
        fetchPrescriptionData(prescriptionId);
      } else {
        form.resetFields();
        setMedicationFields([{ id: Date.now() }]);
        setCurrentPrescription(null);
        setDiscountInput(0);
      }
      form.setFieldsValue({
        pharmacy: pharmacy,
      });
    }

    return () => {
      if (medicationSearchTimeout) {
        clearTimeout(medicationSearchTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, prescriptionId]);

  // Re-validate discount when medication fields change or prescription data loads
  useEffect(() => {
    if (visible && !isPaidNonPremium) {
      if (isEditMode && currentPrescription) {
        // For edit mode, validate after prescription data is loaded
        form.validateFields(['discountAmount']);
      } else if (!isEditMode && medicationFields.length > 0) {
        // For new prescriptions, validate when medications are available
        form.validateFields(['discountAmount']);
      }
    }
  }, [medicationFields, medications, visible, isPaidNonPremium, currentPrescription, isEditMode]);

  const fetchPrescriptionData = async (id) => {
    try {
      setFetchingPrescription(true);
      const response = await apiGetPrescription(id);
      const prescriptionData = response?.data;
      if (prescriptionData) {
        const prescription = Array.isArray(prescriptionData)
          ? prescriptionData[0]
          : prescriptionData;
        setCurrentPrescription(prescription);
        const discountValue = prescription.discountAmount || 0;
        setDiscountInput(discountValue);
        form.setFieldsValue({
          patientId: prescription.patient || prescription.patientId,
          specialInstructions:
            prescription.instruction || prescription.specialInstructions,
          discountAmount: discountValue,
        });

        if (prescription.items && prescription.items.length > 0) {
          const newMedicationFields = prescription.items.map((item, index) => ({
            id: Date.now() + index,
          }));
          setMedicationFields(newMedicationFields);

          setTimeout(() => {
            prescription.items.forEach((item, index) => {
              const fieldId = newMedicationFields[index].id;
              form.setFieldsValue({
                [`medication_${fieldId}`]: item.medication,
                [`dosageAmount_${fieldId}`]: item.dosageAmount,
                [`dosageUnit_${fieldId}`]: item.dosageUnit,
                [`frequency_${fieldId}`]: item.frequency,
                [`durationAmount_${fieldId}`]: item.duration,
                [`durationUnit_${fieldId}`]: item.durationUnit,
              });
            });
          }, 100);
        }
      }
    } catch (error) {
      console.error("Error fetching prescription:", error);
      message.error("Failed to load prescription data");
    } finally {
      setFetchingPrescription(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await apiGetPatients({ is_premium: 1 });
      setPatients(response.data || []);
    } catch (error) {
      console.error("Error fetching patients:", error);
    }
  };

  const fetchMedications = async (searchQuery = "") => {
    try {
      setSearchingMedications(true);
      const params = { page: 1, per_page: 20 };
      if (searchQuery) params.q = searchQuery;
      const response = await apiGetMedications(params);
      setMedications(response.data || []);
    } catch (error) {
      console.error("Error fetching medications:", error);
    } finally {
      setSearchingMedications(false);
    }
  };

  const handleMedicationSearch = (value) => {
    if (medicationSearchTimeout) clearTimeout(medicationSearchTimeout);
    const timeout = setTimeout(() => fetchMedications(value), 500);
    setMedicationSearchTimeout(timeout);
  };

  const handleAddMedication = () =>
    setMedicationFields((prev) => [...prev, { id: Date.now() }]);

  const handleRemoveMedication = (id) => {
    if (medicationFields.length === 1) {
      message.warning("At least one medication is required");
      return;
    }
    setMedicationFields((prev) => prev.filter((field) => field.id !== id));
    form.setFieldsValue({
      [`medication_${id}`]: undefined,
      [`dosageAmount_${id}`]: undefined,
      [`dosageUnit_${id}`]: undefined,
      [`frequency_${id}`]: undefined,
      [`durationAmount_${id}`]: undefined,
      [`durationUnit_${id}`]: undefined,
    });
  };

  const handleMedicationSelect = async (medicationId, fieldId) => {
    try {
      const response = await apiGetMedications({ id: medicationId });
      const medicationData = response?.data;
      if (medicationData) {
        const medication = Array.isArray(medicationData)
          ? medicationData[0]
          : medicationData;
        if (medication) {
          form.setFieldsValue({
            [`dosageAmount_${fieldId}`]: medication.defaultDosageAmount || null,
            [`dosageUnit_${fieldId}`]: medication.defaultDosageUnit || null,
            [`frequency_${fieldId}`]: medication.defaultFrequency || null,
            [`durationAmount_${fieldId}`]: medication.defaultDuration || null,
            [`durationUnit_${fieldId}`]: medication.defaultDurationUnit || null,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching medication details:", error);
      message.error("Failed to load medication details");
    }
  };

  const buildPayloadFromForm = (values) => {
    const items = medicationFields.map((field) => {
      const durationAmount = values[`durationAmount_${field.id}`];
      const durationUnit = values[`durationUnit_${field.id}`];
      const startDate = new Date().toISOString().split("T")[0];

      let endDate = null;
      if (durationUnit !== "Ongoing" && durationAmount) {
        const start = new Date();
        if (durationUnit === "Days")
          start.setDate(start.getDate() + parseInt(durationAmount));
        else if (durationUnit === "Weeks")
          start.setDate(start.getDate() + parseInt(durationAmount) * 7);
        else if (durationUnit === "Months")
          start.setMonth(start.getMonth() + parseInt(durationAmount));
        endDate = start.toISOString().split("T")[0];
      }

      return {
        medication: values[`medication_${field.id}`],
        dosageAmount: parseFloat(values[`dosageAmount_${field.id}`]),
        dosageUnit: values[`dosageUnit_${field.id}`],
        frequency: values[`frequency_${field.id}`],
        duration: parseInt(durationAmount),
        durationUnit: durationUnit,
        startDate,
        endDate,
      };
    });

    return {
      patient: values.patientId,
      pharmacy: pharmacy,
      instruction: values.specialInstructions || null,
      discountAmount: isEditMode ? (values.discountAmount || 0) : 0, // Only include discount for edit mode
      items,
    };
  };

  // status integers used in your description:
  // 1 = Draft, 2 = Approved / Sent to patient, 3 = Paid, 4 = Sent to pharmacy

  const submitWithStatus = async (status) => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const basePayload = buildPayloadFromForm(values);
      const payload = { ...basePayload, status };

      if (isEditMode) {
        await apiUpdatePrescription(prescriptionId, payload);
        message.success(
          status === 1
            ? "Draft updated"
            : status === 4
            ? "Sent to pharmacy"
            : "Updated"
        );
      } else {
        await apiCreatePrescription(payload);
        message.success(
          status === 1 ? "Prescription saved as draft" : "Sent successfully"
        );
      }

      form.resetFields();
      setMedicationFields([{ id: Date.now() }]);
      onSuccess();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        message.error("Please fill in all required fields");
      } else {
        console.error("Error saving prescription:", error);
        message.error(
          error?.response?.data?.message || "Failed to save prescription"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Special function for status-only updates (case: status=3 & !isPremium)
  const submitWithStatusOnly = async (status, prescriptionId) => {
    try {
      setLoading(true);
      const payload = { status }; // Only send status

      await apiUpdatePrescription(prescriptionId, payload);
      message.success("Sent to pharmacy successfully");

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error sending to pharmacy:", error);
      message.error(
        error?.response?.data?.message || "Failed to send to pharmacy"
      );
    } finally {
      setLoading(false);
    }
  };

  // Determine modal footer buttons based on current prescription state and premium flag
  const renderFooterButtons = () => {
    const pres = currentPrescription || {};
    const status = pres.status || 1; // default draft
    const isPremium = Boolean(pres.isPremium);

    // CASES described:
    // case1 => status 1 & !isPremium => Add button Send to Patient (but send to pharmacy only available in edit modal)
    // case2 => status 2 & !isPremium => show Send to Pharmacy
    // case3 => status 3 & !isPremium => in edit show both send to patient and send to pharmacy
    // case4 => status 4 & !isPremium => only view (disable edits)
    // case5 => status 1 & isPremium => editable but in edit only send to pharmacy (status=4)
    // case6 => status 4 & isPremium => only view (disable edits)

    // If not in edit mode, we allow creating:
    if (!isEditMode) {
      // For new prescriptions: show Cancel / Save as Draft / Send to Pharmacy
      return (
        <>
          <Button
            icon={<CloseOutlined />}
            onClick={() => {
              form.resetFields();
              setMedicationFields([{ id: Date.now() }]);
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            icon={<SaveOutlined />}
            onClick={() => submitWithStatus(1)}
            loading={loading}
          >
            Save as Draft
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => submitWithStatus(4)}
            loading={loading}
            className="send-btn"
          >
            Send to Pharmacy
          </Button>
        </>
      );
    }

    // In edit mode: pick actions depending on status & isPremium
    // If status is 4 (sent to pharmacy), editing should be disabled (per case4/case6).
    if (status === 4) {
      // Only Close button (view-only)
      return (
        <>
          <Button
            onClick={() => {
              form.resetFields();
              setMedicationFields([{ id: Date.now() }]);
              onClose();
            }}
          >
            Close
          </Button>
        </>
      );
    }

    // If draft & not premium (case1): editable; show Save as Draft + Send to Patient + Send to Pharmacy (send to pharmacy possibly allowed in edit per earlier text)
    if (status === 1 && !isPremium) {
      return (
        <>
          <Button
            onClick={() => {
              form.resetFields();
              setMedicationFields([{ id: Date.now() }]);
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            icon={<SaveOutlined />}
            onClick={() => submitWithStatus(1)}
            loading={loading}
          >
            Save as Draft
          </Button>
          <Button
            icon={<SendOutlined />}
            onClick={() => submitWithStatus(2)}
            loading={loading}
          >
            Approve & Send to Patient
          </Button>
        </>
      );
    }

    // If draft & premium (case5): editable but only Send to Pharmacy in edit -> status 4
    if (status === 1 && isPremium) {
      return (
        <>
          <Button
            onClick={() => {
              form.resetFields();
              setMedicationFields([{ id: Date.now() }]);
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            icon={<SaveOutlined />}
            onClick={() => submitWithStatus(1)}
            loading={loading}
          >
            Save as Draft
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => submitWithStatus(4)}
            loading={loading}
          >
            Send to Pharmacy
          </Button>
        </>
      );
    }

    // If Approved (status 2) & not premium (case2): show Send to Pharmacy
    if (status === 2 && !isPremium) {
      return (
        <>
          <Button
            onClick={() => {
              form.resetFields();
              setMedicationFields([{ id: Date.now() }]);
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            icon={<SaveOutlined />}
            onClick={() => submitWithStatus(2)}
            loading={loading}
          >
            Save
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => submitWithStatus(4)}
            loading={loading}
            disabled
          >
            Send to Pharmacy
          </Button>
        </>
      );
    }

    // If Paid (status 3) & not premium (case3): only send to pharmacy, all fields disabled
    if (status === 3 && !isPremium) {
      return (
        <>
          <Button
            icon={<CloseOutlined />}
            onClick={() => {
              form.resetFields();
              setMedicationFields([{ id: Date.now() }]);
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => submitWithStatusOnly(4, prescriptionId)}
            loading={loading}
            className="send-btn"
          >
            Send to Pharmacy
          </Button>
        </>
      );
    }

    // Fallback: provide cancel + save
    return (
      <>
        <Button
          onClick={() => {
            form.resetFields();
            setMedicationFields([{ id: Date.now() }]);
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          icon={<SaveOutlined />}
          onClick={() => submitWithStatus(status)}
          loading={loading}
        >
          Save
        </Button>
      </>
    );
  };

  const handleCancel = () => {
    form.resetFields();
    setMedicationFields([{ id: Date.now() }]);
    setDiscountInput(0);
    onClose();
  };

  const getMedicationType = (type) => {
    const typeMap = {
      1: "Tablet",
      2: "Capsule",
      3: "Injection",
      4: "Liquid",
      5: "Patch",
      6: "Inhaler",
      7: "Other",
    };
    return typeMap[type] || "";
  };

  return (
    <Modal
      title={
        <div className="modal-header">
          <span className="text-base font-semibold text-gray-900">
            {isEditMode ? "Edit Prescription" : "Quick Prescription Entry"}
          </span>
          {!isEditMode && (
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={handleAddMedication}
              className="add-medication-btn"
              disabled={isPaidNonPremium}
            />
          )}
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      closable={false}
      footer={renderFooterButtons()}
      width={800}
      className="prescription-modal"
    >
      {fetchingPrescription ? (
        <div className="flex items-center justify-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        <Form form={form} layout="vertical">
          {/* form fields same as before */}
          <div className="modal-content">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Form.Item
                label={<span className="field-label">Patient</span>}
                name="patientId"
                rules={[{ required: true, message: "Please select a patient" }]}
              >
                <Select
                  placeholder="Select patient"
                  disabled={isEditMode || isPaidNonPremium}
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {patients.map((patient) => (
                    <Option key={patient.id} value={patient.id}>
                      {patient.name || patient.fullName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={<span className="field-label">Pharmacy</span>}
                name="pharmacy"
                rules={[
                  { required: true, message: "Please enter pharmacy name" },
                ]}
              >
                <Input disabled placeholder="Patient's preferred pharmacy" />
              </Form.Item>
            </div>

            {medicationFields.map((field, index) => (
              <div key={field.id} className="medication-card">
                <div className="medication-card-header">
                  <span className="text-sm font-semibold text-gray-900">
                    Medication {index + 1}
                  </span>
                  {medicationFields.length > 1 && (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveMedication(field.id)}
                      size="small"
                      disabled={isPaidNonPremium}
                    />
                  )}
                </div>

                <Form.Item
                  label={<span className="field-label">Medication</span>}
                  name={`medication_${field.id}`}
                  rules={[
                    { required: true, message: "Please select a medication" },
                  ]}
                >
                  <Select
                    placeholder="Start typing medication name"
                    disabled={isPaidNonPremium}
                    showSearch
                    onSearch={handleMedicationSearch}
                    filterOption={false}
                    loading={searchingMedications}
                    notFoundContent={
                      searchingMedications ? (
                        <Spin size="small" />
                      ) : (
                        "No medications found"
                      )
                    }
                    onChange={(medicationId) =>
                      handleMedicationSelect(medicationId, field.id)
                    }
                  >
                    {medications.map((med) => (
                      <Option key={med.id} value={med.id}>
                        {med.name}{" "}
                        {med.type && `(${getMedicationType(med.type)})`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <div className="dosage-frequency-row">
                  <div className="dosage-group">
                    <label className="field-label">Dosage</label>
                    <div className="dosage-inputs">
                      <Form.Item
                        name={`dosageAmount_${field.id}`}
                        noStyle
                        rules={[{ required: true, message: "" }]}
                      >
                        <InputNumber placeholder="Amount" controls={false} disabled={isPaidNonPremium} />
                      </Form.Item>
                      <Form.Item
                        name={`dosageUnit_${field.id}`}
                        noStyle
                        rules={[{ required: true, message: "" }]}
                      >
                        <Select placeholder="mg" disabled={isPaidNonPremium}>
                          <Option value="mg">mg</Option>
                          <Option value="g">g</Option>
                          <Option value="mcg">mcg</Option>
                          <Option value="tablet">tablet</Option>
                          <Option value="ml">ml</Option>
                          <Option value="IU">IU</Option>
                          <Option value="Injection">Injection</Option>
                        </Select>
                      </Form.Item>
                    </div>
                  </div>

                  <Form.Item
                    label={<span className="field-label">Frequency</span>}
                    name={`frequency_${field.id}`}
                    rules={[{ required: true, message: "Please select frequency" }]}
                    className="frequency-field"
                  >
                    <Select placeholder="Once daily" disabled={isPaidNonPremium}>
                      <Option value="Once daily">Once daily</Option>
                      <Option value="Twice daily">Twice daily</Option>
                      <Option value="Three times daily">
                        Three times daily
                      </Option>
                    </Select>
                  </Form.Item>
                </div>

                <div className="dosage-frequency-row">
                  <div className="dosage-group">
                    <label className="field-label">Duration</label>
                    <div className="duration-inputs">
                      <Form.Item
                        name={`durationAmount_${field.id}`}
                        noStyle
                        rules={[
                          { required: true, message: "" },
                          { type: "number", max: 31, message: "Maximum 31" },
                        ]}
                      >
                        <InputNumber
                          placeholder="Duration"
                          controls={false}
                          min={0}
                          max={31}
                          disabled={isPaidNonPremium}
                        />
                      </Form.Item>
                      <Form.Item
                        name={`durationUnit_${field.id}`}
                        noStyle
                        rules={[{ required: true, message: "" }]}
                      >
                        <Select placeholder="Days" disabled={isPaidNonPremium}>
                          <Option value="Days">Days</Option>
                          <Option value="Weeks">Weeks</Option>
                          <Option value="Months">Months</Option>
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {isEditMode && (
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddMedication}
                className="w-full mb-4"
                disabled={isPaidNonPremium}
              >
                Add Another Medication
              </Button>
            )}

            {!currentPrescription?.isPremium && isEditMode && medicationFields.length > 0 && (
              <Card className="mb-6 border border-gray-200">
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900">Pricing Information</h4>

                  {/* Display calculated totals with real-time discount */}
                  {(() => {
                    const totals = calculateTotals(discountInput);
                    return (
                      <div className="grid grid-cols-3 gap-4 mb-4" key={`${currentPrescription?.id || 'new'}-${discountInput}`}>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-xs text-gray-600 uppercase">Total Price</div>
                          <div className="text-lg font-semibold text-gray-900">${totals.totalPrice.toFixed(2)}</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-xs text-gray-600 uppercase">Discount</div>
                          <div className="text-lg font-semibold text-green-600">-${discountInput.toFixed(2)}</div>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-xs text-blue-600 uppercase">Final Price</div>
                          <div className="text-lg font-semibold text-blue-900">${totals.finalPrice.toFixed(2)}</div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Discount Amount Input */}
                  <Form.Item
                    label={<span className="field-label">Discount Amount ($)</span>}
                    name="discountAmount"
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const discount = value || 0;
                          const totals = calculateTotals();
                          if (discount > totals.totalPrice) {
                            return Promise.reject(new Error('Discount cannot exceed total price'));
                          }
                          if (discount < 0) {
                            return Promise.reject(new Error('Discount cannot be negative'));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <InputNumber
                      placeholder="0.00"
                      min={0}
                      step={0.01}
                      precision={2}
                      className="w-full"
                      disabled={isPaidNonPremium || (currentPrescription && currentPrescription.status >= 2) || !isEditMode}
                      onChange={(value) => {
                        const numericValue = value || 0;
                        setDiscountInput(numericValue);
                        form.setFieldsValue({ discountAmount: numericValue });
                        form.validateFields(['discountAmount']);
                      }}
                    />
                  </Form.Item>
                </div>
              </Card>
            )}

            <Form.Item
              label={<span className="field-label">Special Instructions</span>}
              name="specialInstructions"
              className="mt-4"
            >
              <TextArea
                rows={4}
                placeholder="Enter any special instructions or notes for the patient/pharmacist"
                disabled={isPaidNonPremium}
              />
            </Form.Item>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default AddPrescriptionModal;
